import { UmsMember, UmsMemberReceiveAddressV2 } from 'types/ums';
import { Trans } from 'react-i18next';
import LOCALS from 'commons/locals';
import {
  deleteMemberReceiveAddressV2,
  updateMemberReceiveAddressV2,
  useGetMemberReceiveAddressList,
} from 'apis/ums';
import { Button, Card, message, Modal, Switch, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useCallback, useMemo, useState } from 'react';
import i18n from 'i18n';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { findLabelByValue } from 'commons/options';
import AddressAdd from './address-add';
import { useCityList } from 'apis/home';
import { Edit, MapPin, Plus, Trash2 } from 'lucide-react';

type MemberAddressListProps = {
  memberId: UmsMember['id'];
};

const CityName = ({
  countryCode,
  cityCode,
}: {
  countryCode: string;
  cityCode: string | null;
}) => {
  const { cityList } = useCityList(countryCode);
  const city = cityCode && cityList.find((i) => i.code === cityCode)?.name;

  return <span>{city || '-'}</span>;
};

const MemberAddressList = ({ memberId }: MemberAddressListProps) => {
  const { memberReceiveAddressList, loading, getAddressList } =
    useGetMemberReceiveAddressList(memberId);
  const { countryOptions } = useAppSelector(selectGlobalInfo);
  const [open, setOpen] = useState(false);
  const [editingAddress, setEditingAddress] =
    useState<UmsMemberReceiveAddressV2>();
  const [updatingId, setUpdatingId] = useState<number>();

  const reload = useCallback(() => {
    getAddressList(memberId);
  }, [getAddressList, memberId]);

  const handleDefaultChange = useCallback(
    (record: UmsMemberReceiveAddressV2, checked: boolean) => {
      setUpdatingId(record.id);
      updateMemberReceiveAddressV2(record.id, {
        name: record.name,
        phoneNumber: record.phoneNumber,
        postCode: record.postCode,
        detailAddress: record.detailAddress,
        province: record.province,
        city: record.city || undefined,
        defaultStatus: checked ? 1 : 0,
      })
        .then(() => {
          message.success(i18n.t(LOCALS.successful_operation));
          reload();
        })
        .finally(() => {
          setUpdatingId(undefined);
        });
    },
    [reload],
  );

  const handleDelete = useCallback(
    (record: UmsMemberReceiveAddressV2) => {
      Modal.confirm({
        title: i18n.t(LOCALS.are_you_sure_to_delete),
        onOk: () =>
          deleteMemberReceiveAddressV2(record.id, memberId).then(() => {
            message.success(i18n.t(LOCALS.successful_operation));
            reload();
          }),
      });
    },
    [memberId, reload],
  );

  const columns: ColumnsType<UmsMemberReceiveAddressV2> = useMemo(() => {
    return [
      {
        title: <Trans i18nKey={LOCALS.name}></Trans>,
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: <Trans i18nKey={LOCALS.phone_number}></Trans>,
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        render: (phoneNumber: UmsMemberReceiveAddressV2['phoneNumber']) => {
          return phoneNumber || '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.country_region}></Trans>,
        dataIndex: 'province',
        key: 'province',
        // 历史遗留问题，country 存到了 province 字段里
        render: (country: UmsMemberReceiveAddressV2['province']) => {
          return findLabelByValue(country, countryOptions);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.state_province_city}></Trans>,
        dataIndex: 'city',
        key: 'city',
        render: (city: string | null, address: UmsMemberReceiveAddressV2) => {
          return <CityName countryCode={address.province} cityCode={city} />;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.zip_code}></Trans>,
        dataIndex: 'postCode',
        key: 'postCode',
      },
      {
        title: <Trans i18nKey={LOCALS.detail_address}></Trans>,
        dataIndex: 'detailAddress',
        key: 'detailAddress',
      },
      {
        title: <Trans i18nKey={LOCALS.default_address}></Trans>,
        dataIndex: 'defaultStatus',
        key: 'defaultStatus',
        render: (
          defaultStatus: UmsMemberReceiveAddressV2['defaultStatus'],
          record,
        ) => {
          return (
            <Switch
              checked={!!defaultStatus}
              loading={updatingId === record.id}
              onChange={(checked) => handleDefaultChange(record, checked)}
            />
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.options}></Trans>,
        key: 'action',
        fixed: 'right',
        render: (_, record: UmsMemberReceiveAddressV2) => {
          return (
            <div className="space-x-2 flex items-center">
              <Button
                type="primary"
                className="flex items-center gap-2"
                onClick={() => {
                  setEditingAddress(record);
                  setOpen(true);
                }}
              >
                <Edit className="w-4 h-4" />
                <Trans i18nKey={LOCALS.edit} />
              </Button>
              <Button
                type="primary"
                danger
                className="flex items-center gap-2"
                onClick={() => handleDelete(record)}
              >
                <Trash2 className="w-4 h-4" />
                <Trans i18nKey={LOCALS.delete} />
              </Button>
            </div>
          );
        },
      },
    ];
  }, [countryOptions, handleDefaultChange, handleDelete, updatingId]);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-base font-medium">
          <MapPin className="w-5 h-5" />
          <Trans i18nKey={LOCALS.shipping_address} />
        </div>
        <Button
          onClick={() => {
            setEditingAddress(undefined);
            setOpen(true);
          }}
          type="primary"
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <Trans i18nKey={LOCALS.XQHQaxYgtY} />
        </Button>
      </div>

      <Table
        bordered
        pagination={false}
        dataSource={memberReceiveAddressList}
        loading={loading}
        rowKey={'id'}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        columns={columns}
        size="small"
      />

      <AddressAdd
        open={open}
        title={editingAddress ? i18n.t(LOCALS.edit) || '' : undefined}
        address={editingAddress || undefined}
        onClose={() => {
          setOpen(false);
          setEditingAddress(undefined);
        }}
        memberId={memberId}
        getLoad={reload}
      />
    </Card>
  );
};

export default MemberAddressList;
